import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Camera, Sparkle, FilePdf, Heart, Check, ShieldCheck } from "@phosphor-icons/react";
import { startDirectCheckout } from "@/lib/api";
import { toast } from "sonner";

/**
 * Landing V2 — AI-first variant.
 * Hero: "Snap. AI fills. Tax-ready."  
 * Hook: the magic moment of receipt → form.
 */
export default function LandingV2() {
  const navigate = useNavigate();
  const { isAuthed, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuthed) navigate("/log");
  }, [isAuthed, loading, navigate]);

  const goSubscribe = async (plan = "yearly") => {
    try { await startDirectCheckout(plan); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout. Try again in a moment."); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#1E2420]">
      {/* Top bar */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#E3E1D9] bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="bg-white rounded-xl px-2 py-1 shadow-sm">
          <img src="/medeasy-logo.png" alt="MedEasy" className="h-10 sm:h-12 w-auto object-contain" />
        </div>
        <Button
          data-testid="v2-header-signin"
          onClick={() => navigate("/login")}
          className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
        >
          Sign in
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-12 sm:pt-20 pb-16">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2E7BC4]/10 text-[#2E7BC4] text-xs uppercase tracking-[0.18em] font-semibold mb-6">
              <Sparkle weight="fill" size={12} /> AI-powered · Built for Canadians
            </div>
            <h1 className="font-[Manrope] text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]">
              Too tired to track<br />
              medical expenses?<br />
              <span className="text-[#2E7BC4]">So was I.</span>
            </h1>
            <p className="text-[#5C6660] mt-6 text-lg sm:text-xl leading-relaxed max-w-xl">
              Point your camera at a parking stub, a prescription, or a gas receipt. MedEasy reads it and fills in the date, vendor, amount, and DIN automatically. <strong>Snap. AI fills. Tax-ready.</strong> Your CRA-ready medical-expense logbook builds itself.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                data-testid="v2-cta-primary"
                onClick={goSubscribe}
                size="lg"
                className="rounded-full h-14 px-8 bg-[#2C4C3B] hover:bg-[#3A634D] text-white text-base font-semibold"
              >
                Start tracking — $49.50/year
                <ArrowRight size={18} className="ml-2" weight="bold" />
              </Button>
              <Button
                data-testid="v2-cta-secondary"
                onClick={() => navigate("/hope-air")}
                size="lg"
                variant="outline"
                className="rounded-full h-14 px-8 border-[#C17767]/40 text-[#C17767] hover:bg-[#C17767]/10 hover:text-[#C17767]"
              >
                <Heart weight="fill" size={16} className="mr-2" /> Why 10% to Hope Air
              </Button>
            </div>

            {/* Trust strip */}
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm text-[#5C6660]">
              <span className="inline-flex items-center gap-1.5"><span aria-hidden>🇨🇦</span> Built in Canada</span>
              <span className="text-[#E3E1D9]">·</span>
              <span className="inline-flex items-center gap-1.5"><Heart weight="fill" size={12} className="text-[#C17767]" /> 10% to Hope Air</span>
              <span className="text-[#E3E1D9]">·</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck weight="fill" size={12} className="text-[#3FA85E]" /> Cancel anytime</span>
            </div>
          </div>

          {/* Right — "magic moment" mock */}
          <div className="relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              {/* Phone frame */}
              <div className="absolute inset-0 rounded-[40px] bg-gradient-to-br from-[#2C4C3B] to-[#1E2420] p-3 shadow-2xl">
                <div className="w-full h-full rounded-[32px] bg-white overflow-hidden flex flex-col">
                  <div className="px-5 pt-6 pb-4 border-b border-[#E3E1D9]">
                    <div className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">New Entry · Auto-filled</div>
                    <div className="font-[Manrope] font-bold text-xl mt-1">Shoppers Drug Mart</div>
                    <div className="text-sm text-[#5C6660]">Pharmacy · Toronto, ON</div>
                  </div>
                  <div className="flex-1 px-5 py-4 space-y-3 text-sm">
                    {[
                      ["Date", "May 14, 2026"],
                      ["Medication", "Atorvastatin 20mg"],
                      ["DIN", "02385791"],
                      ["Total", "$84.20"],
                      ["Insurance", "$67.00"],
                      ["Out-of-pocket", "$17.20"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-3">
                        <span className="text-[#5C6660]">{k}</span>
                        <span className="font-medium text-[#1E2420] inline-flex items-center gap-1.5">
                          {v}
                          <Sparkle weight="fill" size={10} className="text-[#2E7BC4]" />
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="px-5 pb-6 pt-2">
                    <div className="h-11 rounded-full bg-[#2C4C3B] text-white text-sm font-semibold flex items-center justify-center">
                      Save · 1 tap
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating receipt */}
              <div className="absolute -top-8 -left-6 w-32 sm:w-40 rotate-[-8deg] bg-white rounded-lg shadow-xl p-3 text-[10px] font-mono leading-tight text-[#1E2420]">
                <div className="font-bold">SHOPPERS DRUG MART</div>
                <div className="text-[#5C6660]">Bloor & Yonge</div>
                <div className="mt-2 border-t border-dashed border-[#E3E1D9] pt-2">
                  <div>Atorvastatin 20mg</div>
                  <div>DIN 02385791</div>
                  <div className="font-bold mt-1">$ 84.20</div>
                </div>
              </div>
              {/* Sparkle line */}
              <div className="hidden sm:block absolute top-1/2 -left-2 text-[#2E7BC4]">
                <Sparkle weight="fill" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* 3-step strip */}
        <div className="mt-20 sm:mt-28 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {[
            { Icon: Camera, label: "Snap a receipt", text: "Pharmacy, parking, gas, hotel — any medical-related receipt." },
            { Icon: Sparkle, label: "AI fills in everything", text: "Date, vendor, amount, DIN, insurance — auto-populated in 2 seconds." },
            { Icon: FilePdf, label: "Tax-time? One tap.", text: "Download a CRA-ready PDF logbook for your accountant or tax software." },
          ].map(({ Icon, label, text }, i) => (
            <div key={label} className="rounded-2xl bg-white border border-[#E3E1D9] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-[#2C4C3B]/10 text-[#2C4C3B] flex items-center justify-center font-bold text-sm">{i + 1}</div>
                <Icon weight="duotone" size={24} className="text-[#2E7BC4]" />
              </div>
              <div className="font-[Manrope] font-semibold text-lg">{label}</div>
              <p className="text-sm text-[#5C6660] mt-1.5 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        {/* Hope Air strip */}
        <div className="mt-16 rounded-3xl bg-gradient-to-br from-[#2C4C3B] to-[#3A634D] text-white p-8 sm:p-10 flex items-center gap-6 flex-wrap">
          <Heart weight="fill" size={32} className="text-[#C17767] shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <div className="font-[Manrope] font-bold text-xl sm:text-2xl">10% of every payment helps a Canadian get to medical care.</div>
            <p className="text-white/80 text-sm mt-2 max-w-2xl">
              MedEasy donates 10% of every subscription to <strong>Hope Air</strong>, a Canadian charity that arranges free flights, ground travel, and accommodations for people who can&apos;t afford the trip to their appointment.
            </p>
          </div>
          <Button
            data-testid="v2-hope-air-link"
            onClick={() => navigate("/hope-air")}
            variant="outline"
            className="rounded-full bg-white/10 hover:bg-white/20 text-white border-white/20"
          >
            Read more <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center">
          <h2 className="font-[Manrope] text-3xl sm:text-4xl font-bold tracking-tight">
            Stop hunting for receipts in shoeboxes.
          </h2>
          <p className="text-[#5C6660] mt-3 max-w-xl mx-auto">
            $49.50 CAD per year. Cancel any time in one tap.
          </p>
          <Button
            data-testid="v2-cta-final"
            onClick={goSubscribe}
            size="lg"
            className="mt-8 rounded-full h-14 px-10 bg-[#2C4C3B] hover:bg-[#3A634D] text-white text-base font-semibold"
          >
            Start tracking — $49.50 / year
            <ArrowRight size={18} className="ml-2" weight="bold" />
          </Button>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-[#5C6660]">
            <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> Unlimited entries</span>
            <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> AI receipt scanning</span>
            <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> PDF & CSV exports</span>
            <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> Cancel anytime</span>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E3E1D9] px-6 sm:px-10 py-6 text-center text-xs text-[#5C6660] space-y-2">
        <div>MedEasy · Built in Canada 🇨🇦 · 10% of every payment supports <button onClick={() => navigate("/hope-air")} className="underline hover:text-[#2C4C3B]">Hope Air</button></div>
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigate("/terms")} className="hover:text-[#1E2420] underline" data-testid="v2-footer-terms">Terms</button>
          <span className="text-[#E3E1D9]">·</span>
          <button onClick={() => navigate("/privacy")} className="hover:text-[#1E2420] underline" data-testid="v2-footer-privacy">Privacy</button>
        </div>
      </footer>
    </div>
  );
}
